<template>
    <div>
        <div class="row">
            <div class="col-12">
                <Cart
                    v-if="typeIsCart"
                    :appCode="appCode"
                    :appId="appId"
                    :enableExtendedCalendar="enableExtendedCalendar"
                    v-model="cart"
                    name="cart"
                    v-validate="`empty_cart|min_amount_cents|coupon_has_some_valid_cart_item:${appId}`"
                    :helperText="errors.first('cart')"
                    :error="errors.has('cart')"
                    @change="() => handleCartChange()"
                    :dashboardUrl="dashboardUrl"
                    :useDashboardSeatMap="useDashboardSeatMap"
                />

                <TextInput 
                    v-if="typeIsPayment"
                    v-model="value"
                    label="Valor"
                    name="value"
                    type="number"
                    :disabled="false"
                    :required="true"
                    min="1"
                    v-validate="'required'"
                    :helperText="errors.first('value')"
                    :error="errors.has('value')"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-md-6">
                <TextInput 
                    v-model="consumer"
                    label="Consumidor"
                    type="text"
                    name="consumer"
                    :disabled="false"
                    :required="true"
                    v-validate="'required'"
                    :helperText="errors.first('customer.name')"
                    :error="errors.has('consumer')"
                />
            </div>

            <div class="col-6">
                <TextInput 
                    v-model="identity"
                    v-if="typeIsPayment"
                    label="Documento"
                    type="text"
                    name="identity"
                    :disabled="false"
                    :required="true"
                    v-validate="'required'"
                    :helperText="errors.first('customer.identity') || (!validateIdentity() && identity ? 'Documento inválido' : '')"
                    :error="errors.has('identity') || (!validateIdentity() && identity)"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-6">
                <Select 
                    v-model="saleOrigin"
                    :options="saleOrigins"
                    label="Origem de Vendas"
                    :error="false"
                    :disabled="false"
                    :required="false"
                    :disableEmptyOption="false"
                    :getOptionLabel="option => `${option.name} (${option.code})`"
                    :loading="saleOriginsLoading"
                    loadingText="Carregando origens..."
                />
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <TextArea 
                    v-model="customMessage"
                    label="Mensagem de boas-vindas"
                    type="text"
                    :error="false"
                    :disabled="false"
                    :required="false"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <DateTimePicker
                    v-model="expirationDateTime"
                    type="datetime"
                    format="DD-MM-YYYY HH:mm"
                    label="Data e hora limite de pagamento"
                    name="expirationDateTime"
                    :disabled="false"
                    :required="false"
                    v-validate="'required'"
                    :helperText="errors.first('expirationDateTime')"
                    :error="errors.has('expirationDateTime')"
                    :disabledDate="disabledDate"
                    :disabledTime="disabledTime"

                />
            </div>
        </div>

        <div class="row">
            <div class="col-md-6">
                <Checkbox 
                    v-model="sendEmail"
                    label="Enviar link por email"
                    :error="false"
                    :disabled="false"
                    :required="false"
                />

                <TextInput 
                    v-model="email"
                    v-if="sendEmail"
                    label="Email do Consumidor"
                    type="text"
                    name="email"
                    v-validate="'email|required'"
                    :helperText="errors.first('customer.email')"
                    :error="errors.has('email')"
                    :disabled="false"
                    :required="false"
                />
            </div>

            <div class="col-md-6">
                <Checkbox 
                    v-model="sendSms"
                    label="Enviar link por SMS"
                    :error="false"
                    :disabled="false"
                    :required="false"
                />

                <TextInput 
                    v-model="phone"
                    v-if="sendSms"
                    name="phone"
                    label="Telefone do Consumidor"
                    type="text"
                    :disabled="false"
                    :required="false"
                    :mask="['(##) ####-####', '(##) #####-####']"
                    v-validate="'required'"
                    :helperText="errors.first('customer.phone')"
                    :error="errors.has('phone')"
                />
            </div>
        </div>

        <hr />

        <div class="row">
            <div class="col-12">
                <PaymentSelector
                    v-model="payment"
                    :appCode="appCode"
                    :appId="appId"
                    :isOptional="false"
                    v-validate="'direct-link-payment'"
                    name="payment"
                    :helperText="errors.first('payment')"
                    :error="errors.has('payment')"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <Checkbox
                    v-model="trustMode"
                    label="Habilitar Modo de Confiança"
                    :error="false"
                    :disabled="false"
                    :required="true"
                />
                <small v-if="trustMode" class="invalid-feedback">Atenção! Com o modo de confiança ativado esta transação<br/> passará por um modelo mais permissivo de análise do antifraude.</small>
            </div>
        </div>

        <div v-if="helperText" class="row">
            <div class="col-12 text-center">
                <small
                    class="form-text helperText"
                    v-bind:class="{ 'invalid-feedback': error, 'text-muted': !error }"
                >
                    {{ helperText }}
                </small>
            </div>
        </div>

        <div class="row">
            <div class="col-md-6 offset-md-6">
                <div class="row">
                    <div class="col-md-6">
                        <Button type="button" v-if="showBackButton" :onClick="() => handleBack()" isOutlined primary>Voltar</Button>
                    </div>
                    
                    <div class="col-md-6">
                        <Button type="submit" v-if="showSaveButton" :disabled="disabledButtonSave" :onClick="() => handleSave()">Salvar</Button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import TextInput from '../../TextInput';
import TextArea from '../../TextArea';
import Checkbox from '../../Checkbox';
import Select from '../../Select';
import Cart from './Cart';
import PaymentSelector from './PaymentSelector';
import Button from '../../Button';
import DateTimePicker from '../../DateTimePicker';
import SellerApi from '../../../services/SellerApi';
import { cpf, cnpj } from 'cpf-cnpj-validator';

export default {
    components: { 
        Button, Cart, TextInput, TextArea, Checkbox, Select, DateTimePicker, PaymentSelector
    },
    props: {
        appCode: String,
        appId: String,
        enableExtendedCalendar: {
            type: Boolean,
            required: false,
            default: false
        },
        showSaveButton: {
            type: Boolean,
            required: false,
            default: false
        },
        showBackButton: {
            type: Boolean,
            required: false,
            default: false
        },
        type: {
            type: String,
            required: false,
            default: 'sale'
        },
        onSuccess: {
            type: Function,
            required: false,
            default: ()=>{}
        },
        onFail: {
            type: Function,
            required: false,
            default: ()=>{}
        },
        onBack: {
            type: Function,
            required: false,
            default: ()=>{}
        },
        helperText: {
            type: String,
            required: false,
            default: ''
        },
        error: {
            type: Boolean,
            required: false,
            default: false
        },
        dashboardUrl: {
            type: String,
            required: true,
        },
        useDashboardSeatMap: {
            type: Boolean,
            required: true,
        },
    },
    data(){
        return {
            value: 1,
            payment: {
                enablePlannePay: true,
                antifraud: false,
                maxInstallments: 12,
                minInstallmentValue: 1,
                selected: []
            },
            saleOriginsLoading: true,
            saleOrigins: [],
            cart: {},
            customMessage: "",
            consumer: "",
            cpfcnpj: "",
            identity: "",
            phone: "",
            email: "",
            sendEmail: true,
            sendSms: false,
            expirationDateTime: null,
            saleOrigin: null,
            trustMode: false,
            disabledButtonSave: false,
        };
    },
    computed: {
        typeIsCart(){
            return this.type === 'cart';
        },
        typeIsPayment(){
            return this.type === 'payment';
        },
    },
    methods: {
        handleCartChange() {
            this.$validator.validate("cart")
        },
        disabledDate(date) {
            return (
                new Date(date).setHours(0, 0, 0, 0) <
                new Date().setHours(0, 0, 0, 0)
            );
        },
        disabledTime(date) {
            return date < new Date();
        },
        handleBack() {
            this.onBack()
        },
        getIdentityType(identity) {
            if (cpf.isValid(identity ?? '')) {
                return 'cpf';
            }
            
            return 'cnpj';
        },
        generatePayload() {
            const payload = {
                type: this.type,
                customer: {
                    name: this.consumer,
                    identity: this.identity && this.identity.replace(/\D/g, ''),
                    identity_type: this.getIdentityType(this.identity) ?? null,
                },               
                enable_payment: this.payment.enablePlannePay,
                id_sale_origin: this.saleOrigin?.id,
                custom_message: this.customMessage,
                max_installments: this.payment.maxInstallments,
                min_installment_amount_cents: this.payment.minInstallmentValue,
                expires_at: this.expirationDateTime,
                paymentMethodIds: this.payment.selected.map((payment) => payment.id),
                trustMode: this.trustMode,
            }

            if (this.typeIsPayment) {
                payload.total_amount_cents = this.value * 100
            } else if (this.typeIsCart) {
                payload.cart = {
                    couponId: this.cart.coupon?.id,
                    items: this.cart.items.map((item)=>{
                        return {
                            productId: item.product.id,
                            scheduleDate: item.date?.date,
                            scheduleTimeMinutes: item.time?.value,
                            tariffs: item.tariffs.map((tariff) => ({
                                id: tariff.tariff.id,
                                quantity: tariff.amount
                            })),
                            additionals: item.additionals?.map((additional) => {
                                return {
                                    id: additional.id,
                                    enabled: additional.enabled,
                                    input_value: additional.input_value
                                }
                            }) || [],
                            resources: item.resources.map((resource) => {
                                return {
                                    id: resource.id,
                                }
                            })
                        }
                    })
                }
            }

            if (this.sendEmail) {
                payload.customer.email = this.email
            }

            if (this.sendSms) {
                payload.customer.phone = `+55${this.phone}`
            }

            return payload
        },
        handleSave() {
            if (!this.validateIdentity()) {
                return;
            }

            this.disabledButtonSave = true;
            this.$validator.validate().then(async valid => {
                if (valid) {
                    const payload = this.generatePayload()

                    SellerApi.DirectLink.create(this.appCode, payload).then((response) => {
                        this.onSuccess(response)
                    }).catch((error) => {
                        this.onFail(error)
                    }).finally(() => {
                        this.disabledButtonSave = false;
                    });
                } else {
                    this.disabledButtonSave = false;
                }
            })
        },
        getSaleOrigins() {
            this.saleOriginsLoading = true
            
            SellerApi.SaleOrigin.list(this.appCode).then((response) => {
                this.saleOriginsLoading = false

                this.saleOrigins = response
            }).catch((error) => {
                this.saleOriginsLoading = false

                console.error(error)
            })
        },
        validateIdentity() {
            if (!this.identity) {
                return true;
            }

            return cpf.isValid(this.identity ?? '') || cnpj.isValid(this.identity ?? '');
        },
    },
    mounted() {
        this.getSaleOrigins()
    }
}
</script>

<style scoped lang="scss">
.helperText{
    margin-bottom: 20px;
}
</style>
